// src/pages/Reports/IssuanceReporting/IssuanceTicketLookup/SingleTicketLookupTab.tsx
// RPT-015 (README.md mục 7) — tra cứu nhanh trạng thái 1 vé cụ thể. Không phân trang/export (trả
// về 1 object duy nhất) — bắt buộc chọn kỳ phát hành + nhập đủ ký hiệu + số vé trước khi tra cứu.

import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Input, Label, Row, Spinner } from 'reactstrap';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'react-toastify';

import { lookupTicketReportApi } from 'helpers/fakebackend_helper';
import { buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import { formatDateTime } from '../shared/formatters';
import { TicketLookupReport } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const STATE_BADGE: Record<string, string> = {
  ConKho: 'success',
  DaGiao: 'primary',
  DaThuHoi: 'warning',
  DaHuy: 'danger',
};

const SingleTicketLookupTab: React.FC = () => {
  const [planId, setPlanId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TicketLookupReport | null>(null);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap = useMemo(() => buildAgentMap(agents), [agents]);

  const canSubmit = !!planId && !!symbol.trim() && !!ticketNumber.trim();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setResult(null);
    try {
      if (agents.length === 0) loadAgentsWithCache().then(setAgents);
      const res = await lookupTicketReportApi({
        IssuancePlanId: planId,
        Symbol: symbol.trim(),
        TicketNumber: ticketNumber.trim(),
      });
      setResult((res as any)?.data ?? res);
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tra_cuu_ve_message'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <Row className="g-3 mb-3 align-items-end">
          <Col md={3}>
            <Label className="form-label">{st('ky_phat_hanh_label')} <span className="text-danger">*</span></Label>
            <IssuancePlanSelect requireSelection value={planId} onChange={setPlanId} />
          </Col>
          <Col md={3}>
            <Label className="form-label">{st('ky_hieu_label')} <span className="text-danger">*</span></Label>
            <Input required type="text" placeholder={st('vd_n_placeholder')} value={symbol} onChange={(e) => setSymbol(e.target.value)} />
          </Col>
          <Col md={3}>
            <Label className="form-label">{st('so_ve_label')} <span className="text-danger">*</span></Label>
            <Input required type="text" placeholder={st('vd_000123_placeholder')} value={ticketNumber} onChange={(e) => setTicketNumber(e.target.value)} />
          </Col>
          <Col md={3}>
            <Button color="primary" type="submit" disabled={!canSubmit || loading} className="w-100">
              {loading ? <Spinner size="sm" className="me-1" /> : <i className="ri-search-line align-bottom me-1"></i>}
              {st('tra_cuu_button')}
            </Button>
          </Col>
        </Row>
      </Form>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : result ? (
        <div className="border rounded p-4">
          <Row className="g-4">
            <Col lg={result.qrUrl ? 8 : 12}>
              <Row className="g-3">
                <Col md={6}><strong>{st('ky_phat_hanh_text')}</strong> {result.planCode}</Col>
                <Col md={6}><strong>{st('loai_ve_text')}</strong> {result.ticketTypeName || result.ticketType}</Col>
                <Col md={6}><strong>{st('ky_hieu_so_ve_text')}</strong> {result.symbol} / {result.ticketNumber}</Col>
                <Col md={6}><strong>{st('kho_text')}</strong> {result.warehouseLabel}</Col>
                <Col md={6}>
                  <strong>{st('trang_thai_hien_tai_text')}</strong>{' '}
                  <span className={`badge bg-${STATE_BADGE[result.currentState] || 'secondary'}-subtle text-${STATE_BADGE[result.currentState] || 'secondary'}`}>
                    {result.currentStateName}
                  </span>
                </Col>
                {result.agentId != null && (
                  <Col md={6}>
                    <strong>{st('dai_ly_text')}</strong> {getAgentName(agentMap, result.agentId)} ({getAgentCode(agentMap, result.agentId)})
                  </Col>
                )}
                {result.expectedDrawDate && <Col md={6}><strong>{st('ngay_du_kien_xo_text')}</strong> {formatDateTime(result.expectedDrawDate)}</Col>}
                {result.deliveredAt && <Col md={6}><strong>{st('ngay_giao_text')}</strong> {formatDateTime(result.deliveredAt)}</Col>}
                {result.recalledAt && <Col md={6}><strong>{st('ngay_thu_hoi_text')}</strong> {formatDateTime(result.recalledAt)}</Col>}
                {result.cancelledAt && <Col md={6}><strong>{st('ngay_huy_text')}</strong> {formatDateTime(result.cancelledAt)}</Col>}
              </Row>
            </Col>

            {result.qrUrl && (
              <Col lg={4}>
                <div className="border rounded p-3 text-center h-100 d-flex flex-column align-items-center justify-content-center">
                  <div className="fw-semibold mb-2">
                    <i className="ri-qr-code-line align-bottom me-1" />{st('ma_qr_tren_ve_text')}
                  </div>
                  <QRCodeCanvas value={result.qrUrl} size={160} marginSize={2} />
                  <a
                    href={result.qrUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="d-block mt-2 small text-break"
                    style={{ maxWidth: 220, wordBreak: 'break-all' }}
                  >
                    {result.qrUrl}
                  </a>
                </div>
              </Col>
            )}
          </Row>
        </div>
      ) : (
        <Alert color="info" className="mb-0">{st('nhap_du_thong_tin_va_bam_message_2')}</Alert>
      )}
    </>
  );
};

export default SingleTicketLookupTab;
